/**
 * Whether a Vorgang's facade may still change, and on what terms (spec
 * 2026-10-15 §4) — read by both facade gestures: „Maß ändern“ on the board
 * and „Form ändern …“ on the board's head and in the record.
 *
 * - `open` — the gesture goes straight through.
 * - `approved` — it goes through after a question: a released calculation
 *   stops being released the moment the Stand moves (D-11/E-52), and the
 *   person has to know that before, not after.
 * - `sent` — no gesture at all. „Angebot raus“ freezes the Vorgang
 *   (`FROZEN_STAGES = {sent, won, lost}` on the server, 409); a change is a
 *   new Vorgang.
 * - `cancelled` — no gesture either; the way back is „Wiederherstellen“ in
 *   the record (spec §5).
 *
 * In `lib/` and not under `features/planning`, though the board is its first
 * reader: the record reads it too, and features do not import each other
 * (R-W1, `eslint.config.js` — the precedent is `caseGone.ts`).
 */
import { ProblemError, type CaseStatus } from "@/api";

import { recordOf } from "./noStand";

export type FacadeEditability = "open" | "approved" | "sent" | "cancelled";

/** The three stages after „Angebot raus“ — the server's own `FROZEN_STAGES`. */
const AFTER_SENT: ReadonlySet<CaseStatus> = new Set<CaseStatus>(["sent", "won", "lost"]);

export function facadeEditability(status: CaseStatus): FacadeEditability {
  if (AFTER_SENT.has(status)) return "sent";
  if (status === "cancelled") return "cancelled";
  if (status === "quote_approved") return "approved";
  return "open";
}

/** What stands where a gesture would be, once the offer is out. */
export const FACADE_SENT = "Angebot ist raus — Änderungen über einen neuen Vorgang.";

/** And on a cancelled Vorgang — the record is where „Wiederherstellen“ is. */
export const FACADE_CANCELLED = "Vorgang ist storniert — stelle ihn in der Akte wieder her.";

/**
 * `/vorgang/:id?wiederherstellen=1` opens „Wiederherstellen“ on arrival —
 * the way back from the board and the calculation of a cancelled Vorgang,
 * as `?stornieren=1` is the register's way to „Vorgang stornieren …“. One
 * dialog, in the record; the parameter leaves the address once read.
 */
export const RESTORE_PARAM = "wiederherstellen";

export function restorePath(caseId: string): string {
  return `${recordOf(caseId)}?${RESTORE_PARAM}=1`;
}

/**
 * Whether a refused write says the Vorgang was cancelled under the screen —
 * the 409 every write route answers then (`ConflictOut`, `reasons:
 * ["case_cancelled"]`, spec §5). Read off `reasons`, never off the sentence:
 * the server's wording is for a person, the code is for this check.
 */
export function refusedAsCancelled(error: unknown): boolean {
  if (!(error instanceof ProblemError) || error.status !== 409) return false;
  const raw = error.problem.raw;
  if (typeof raw !== "object" || raw === null) return false;
  const reasons = (raw as { reasons?: unknown }).reasons;
  return Array.isArray(reasons) && reasons.includes("case_cancelled");
}
